import { useEffect, useState } from 'react';
import { DEFAULT_KEYMAP, type KeyMap } from '../input/InputManager';
import {
  KEYMAP_BUTTON_LABELS,
  KEYMAP_BUTTONS,
  bindKey,
  cloneKeymap,
  codesForButton,
  formatKeyCode,
  unbindKey,
  type KeymapButton,
} from '../input/keymap';

export interface KeymapSettingsProps {
  keymap: KeyMap;
  onChange: (map: KeyMap) => void;
  onMessage?: (message: string, tone?: 'success' | 'error' | 'info') => void;
}

export function KeymapSettings({ keymap, onChange, onMessage }: KeymapSettingsProps) {
  const [listening, setListening] = useState<KeymapButton | null>(null);

  useEffect(() => {
    if (!listening) return;

    const onKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.repeat) return;

      if (e.code === 'Escape') {
        setListening(null);
        return;
      }

      const result = bindKey(keymap, listening, e.code);
      if (!result.ok) {
        onMessage?.(result.reason, 'error');
        return;
      }
      onChange(result.map);
      onMessage?.(`${KEYMAP_BUTTON_LABELS[listening]} ← ${formatKeyCode(e.code)}`, 'success');
      setListening(null);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [listening, keymap, onChange, onMessage]);

  return (
    <div className="space-y-2">
      <p className="text-[0.7rem] leading-relaxed text-slate-500">
        ボタンを押してから割り当てたいキーを押してください。同じキーは自動で上書きされます。Esc
        でキャンセル。
      </p>

      {listening && (
        <p className="rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-xs text-accent">
          「{KEYMAP_BUTTON_LABELS[listening]}」に割り当てるキーを押してください…
        </p>
      )}

      <ul className="divide-y divide-base-700 overflow-hidden rounded-lg border border-base-700">
        {KEYMAP_BUTTONS.map((button) => {
          const codes = codesForButton(keymap, button);
          return (
            <li key={button} className="flex flex-wrap items-center gap-2 bg-base-900/60 px-3 py-2">
              <span className="w-14 shrink-0 text-xs font-medium text-slate-300">
                {KEYMAP_BUTTON_LABELS[button]}
              </span>
              <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                {codes.length === 0 ? (
                  <span className="text-[0.65rem] text-slate-600">未割当</span>
                ) : (
                  codes.map((code) => (
                    <button
                      key={code}
                      type="button"
                      className="rounded-md border border-base-600 bg-base-800 px-1.5 py-0.5 text-[0.65rem] text-slate-200 hover:border-red-500/60 hover:text-red-300"
                      title="クリックで解除"
                      onClick={() => onChange(unbindKey(keymap, code))}
                    >
                      {formatKeyCode(code)}
                      <span className="ml-1 text-slate-500">×</span>
                    </button>
                  ))
                )}
              </div>
              <button
                type="button"
                className={`btn-ghost !min-h-0 shrink-0 !px-2 !py-1 !text-[0.65rem] ${
                  listening === button ? 'border-accent text-accent' : ''
                }`}
                onClick={() => setListening((cur) => (cur === button ? null : button))}
              >
                {listening === button ? '待機中' : '追加'}
              </button>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        className="btn-ghost w-full !text-xs"
        onClick={() => {
          onChange(cloneKeymap(DEFAULT_KEYMAP));
          setListening(null);
          onMessage?.('キーボード割り当てを既定に戻しました', 'success');
        }}
      >
        既定のキー配置に戻す
      </button>
    </div>
  );
}
